<template>
    <div class="shrink-0 border-b border-default bg-(--ui-bg-chrome)">
        <!-- Top Bar: Brand, Centered Quick Status Badges, System, Color Mode & User Profile -->
        <header class="min-h-14 py-1.5 md:py-0 flex flex-wrap items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 border-b border-default/60">
            <!-- Left: Brand, Search Trigger & Mobile Menu -->
            <div class="order-1 flex items-center gap-2 sm:gap-2.5 min-w-0 shrink-0">
                <button
                    type="button"
                    class="md:hidden inline-flex items-center justify-center p-1.5 rounded-lg border border-default bg-(--ui-bg-elevated)/60 hover:bg-(--ui-bg-elevated) text-highlighted cursor-pointer transition-colors"
                    :aria-label="$t('layout.open_menu')"
                    @click="toggleMenu"
                >
                    <UIcon name="i-lucide-menu" class="size-4 text-primary" />
                </button>

                <!-- Mobile Search Button -->
                <button
                    type="button"
                    class="sm:hidden inline-flex items-center justify-center p-1.5 rounded-lg border border-default bg-(--ui-bg-elevated)/60 hover:bg-(--ui-bg-elevated) text-highlighted cursor-pointer transition-colors"
                    :aria-label="$t('search.placeholder_short')"
                    :title="$t('search.tooltip')"
                    @click="openGlobalSearch()"
                >
                    <UIcon name="i-lucide-search" class="size-4 text-primary" />
                </button>

                <RouterLink to="/dashboard" class="flex items-center gap-2.5 shrink-0">
                    <img src="/logo.svg" alt="" class="size-6 shrink-0">
                    <div class="leading-tight">
                        <span class="text-sm font-semibold text-highlighted">{{ $t('brand.product_name') }}</span>
                        <span class="font-mono text-[9px] uppercase tracking-wider text-dimmed block">{{ $t('brand.product_line') }} &middot; v{{ appVersion }}</span>
                    </div>
                </RouterLink>

                <!-- Full Global Search Trigger Button (Desktop >= lg) -->
                <button
                    type="button"
                    class="hidden xl:inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-muted hover:text-highlighted bg-(--ui-bg-elevated)/60 border border-default hover:border-primary/50 hover:bg-(--ui-bg-elevated) transition-all cursor-pointer shadow-2xs group ml-1"
                    :title="$t('search.tooltip')"
                    @click="openGlobalSearch()"
                >
                    <UIcon name="i-lucide-search" class="size-3.5 text-primary group-hover:scale-110 transition-transform" />
                    <span class="text-xs text-muted group-hover:text-highlighted">{{ $t('search.placeholder_short') }}</span>
                    <div class="flex items-center gap-0.5 ml-1">
                        <UKbd size="xs" variant="subtle">{{ isMac ? '⌘' : 'Ctrl' }}</UKbd>
                        <UKbd size="xs" variant="subtle">K</UKbd>
                    </div>
                </button>

                <!-- Compact Search Trigger Button (sm & md) -->
                <button
                    type="button"
                    class="hidden sm:inline-flex xl:hidden items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-muted hover:text-highlighted bg-(--ui-bg-elevated)/60 border border-default hover:border-primary/50 transition-all cursor-pointer shadow-2xs group ml-1"
                    :title="$t('search.tooltip')"
                    @click="openGlobalSearch()"
                >
                    <UIcon name="i-lucide-search" class="size-3.5 text-primary group-hover:scale-110 transition-transform" />
                    <UKbd size="xs" variant="subtle">{{ isMac ? '⌘' : 'Ctrl' }}K</UKbd>
                </button>
            </div>

            <!-- Center: clickable status badges. Between md and 2xl only the dot and count show (Paused and Unknown hide below lg) (the label is in the tooltip), so the header stays one row -->
            <div class="order-3 w-full md:order-2 md:w-auto md:flex-1 flex items-center justify-center flex-wrap md:flex-nowrap gap-1.5 sm:gap-2 pt-1.5 md:pt-0 border-t md:border-t-0 border-default/40 text-xs">
                <button
                    type="button"
                    title="Click to inspect all UP sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-success-50 dark:bg-success-950/40 text-success-600 dark:text-success-400 font-mono text-[11px] font-medium border border-success-200 dark:border-success-800/60 hover:bg-success-100 dark:hover:bg-success-900/50 transition-all cursor-pointer"
                    :class="{ 'ring-2 ring-success/60 shadow-xs scale-[1.03]': activeStatusQuery === 'UP' }"
                    @click="selectStatusFilter('UP')"
                >
                    <span class="size-1.5 rounded-full bg-success" />
                    <span>{{ statusCounts.up }}<span class="md:max-2xl:hidden"> Up</span></span>
                </button>
                <button
                    type="button"
                    title="Click to inspect all DOWN sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-error-50 dark:bg-error-950/40 text-error-600 dark:text-error-400 font-mono text-[11px] font-medium border border-error-200 dark:border-error-800/60 hover:bg-error-100 dark:hover:bg-error-900/50 transition-all cursor-pointer"
                    :class="[
                        statusCounts.down === 0 && activeStatusQuery !== 'DOWN' ? 'opacity-65' : '',
                        activeStatusQuery === 'DOWN' ? 'ring-2 ring-error/70 shadow-xs scale-[1.03]' : '',
                    ]"
                    @click="selectStatusFilter('DOWN')"
                >
                    <span class="size-1.5 rounded-full bg-error" :class="{ 'animate-ping': statusCounts.down > 0 }" />
                    <span>{{ statusCounts.down }}<span class="md:max-2xl:hidden"> Down</span></span>
                </button>
                <button
                    type="button"
                    title="Click to inspect all WARN sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-warning-50 dark:bg-warning-950/40 text-warning-600 dark:text-warning-400 font-mono text-[11px] font-medium border border-warning-200 dark:border-warning-800/60 hover:bg-warning-100 dark:hover:bg-warning-900/50 transition-all cursor-pointer"
                    :class="[
                        statusCounts.warning === 0 && activeStatusQuery !== 'WARNING' ? 'opacity-65' : '',
                        activeStatusQuery === 'WARNING' ? 'ring-2 ring-warning/70 shadow-xs scale-[1.03]' : '',
                    ]"
                    @click="selectStatusFilter('WARNING')"
                >
                    <span class="size-1.5 rounded-full bg-warning" />
                    <span>{{ statusCounts.warning }}<span class="md:max-2xl:hidden"> Warn</span></span>
                </button>
                <button
                    type="button"
                    title="Click to inspect all ACKNOWLEDGED sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-primary/10 text-primary font-mono text-[11px] font-medium border border-primary/30 hover:bg-primary/10 transition-all cursor-pointer"
                    :class="[
                        statusCounts.acknowledged === 0 && activeStatusQuery !== 'ACKNOWLEDGED' ? 'opacity-65' : '',
                        activeStatusQuery === 'ACKNOWLEDGED' ? 'ring-2 ring-primary/70 shadow-xs scale-[1.03]' : '',
                    ]"
                    @click="selectStatusFilter('ACKNOWLEDGED')"
                >
                    <span class="size-1.5 rounded-full bg-primary" />
                    <span>{{ statusCounts.acknowledged }}<span class="md:max-2xl:hidden"> Ack</span></span>
                </button>
                <button
                    type="button"
                    title="Click to inspect all PAUSED sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 md:max-lg:hidden bg-info/10 text-info font-mono text-[11px] font-medium border border-info/30 hover:bg-info/10 transition-all cursor-pointer"
                    :class="[
                        statusCounts.paused === 0 && activeStatusQuery !== 'PAUSED' ? 'opacity-65' : '',
                        activeStatusQuery === 'PAUSED' ? 'ring-2 ring-info/70 shadow-xs scale-[1.03]' : '',
                    ]"
                    @click="selectStatusFilter('PAUSED')"
                >
                    <span class="size-1.5 rounded-full bg-info" />
                    <span>{{ statusCounts.paused }}<span class="md:max-2xl:hidden"> Paused</span></span>
                </button>
                <button
                    type="button"
                    title="Click to inspect all UNKNOWN sensors"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 md:max-lg:hidden bg-elevated text-muted font-mono text-[11px] font-medium border border-default hover:bg-muted/80 transition-all cursor-pointer"
                    :class="{ 'ring-2 ring-(--ui-border-accented)/70 shadow-xs scale-[1.03]': activeStatusQuery === 'UNKNOWN' }"
                    @click="selectStatusFilter('UNKNOWN')"
                >
                    <span class="size-1.5 rounded-full bg-accented" />
                    <span>{{ statusCounts.unknown }}<span class="md:max-2xl:hidden"> Unknown</span></span>
                </button>
            </div>

            <!-- Right: System, Language, Color Mode, User Profile, Sign Out -->
            <div class="order-2 md:order-3 flex items-center shrink-0 gap-2 sm:gap-2.5">
                <!-- System Menu Dropdown -->
                <UDropdownMenu :items="systemMenu" :content="{ align: 'end', side: 'bottom' }">
                    <button
                        type="button"
                        class="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-dimmed hover:text-highlighted bg-(--ui-bg-elevated)/60 border border-default hover:bg-(--ui-bg-elevated) transition-colors cursor-pointer"
                    >
                        <UIcon name="i-lucide-hard-drive" class="size-3.5" />
                        <span class="hidden lg:inline">System</span>
                        <UIcon name="i-lucide-chevron-down" class="size-3 text-dimmed" />
                    </button>
                </UDropdownMenu>

                <!-- Language Switcher Dropdown -->
                <UDropdownMenu :items="languageMenu" :content="{ align: 'end', side: 'bottom' }">
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-dimmed hover:text-highlighted bg-(--ui-bg-elevated)/60 border border-default hover:bg-(--ui-bg-elevated) transition-colors cursor-pointer"
                        :title="$t('languages.change_language')"
                    >
                        <span class="text-sm leading-none">{{ currentLocaleFlag }}</span>
                        <span class="font-mono text-[11px] uppercase font-bold text-highlighted">{{ currentLocaleCode }}</span>
                        <UIcon name="i-lucide-chevron-down" class="size-3 text-dimmed" />
                    </button>
                </UDropdownMenu>

                <!-- Color Mode Toggle -->
                <ColorModeToggle />

                <!-- Help & Documentation shortcut button -->
                <RouterLink
                    :to="{ name: 'about', query: { tab: 'help' } }"
                    class="inline-flex items-center justify-center p-1.5 rounded-lg text-dimmed hover:text-highlighted bg-(--ui-bg-elevated)/60 border border-default hover:bg-(--ui-bg-elevated) transition-colors"
                    :title="$t('nav.about')"
                >
                    <UIcon name="i-lucide-help-circle" class="size-4" />
                </RouterLink>

                <!-- User Profile Dropdown -->
                <UDropdownMenu :items="userMenu" :content="{ align: 'end', side: 'bottom' }">
                    <button
                        type="button"
                        class="flex items-center gap-2 rounded-lg px-2 py-1 text-left bg-(--ui-bg-elevated)/60 border border-default hover:bg-(--ui-bg-elevated) transition-colors cursor-pointer"
                    >
                        <span class="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary text-xs font-semibold uppercase">
                            {{ initials }}
                        </span>
                        <span class="hidden xl:block text-xs font-medium text-highlighted">{{ userName }} (Admin)</span>
                        <UIcon name="i-lucide-chevron-down" class="size-3.5 text-dimmed" />
                    </button>
                </UDropdownMenu>

                <!-- Direct Sign Out affordance -->
                <button
                    type="button"
                    class="hidden md:inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-dimmed hover:text-error-500 bg-(--ui-bg-elevated)/60 border border-default hover:border-error-300 dark:hover:border-error-800 hover:bg-(--ui-bg-elevated) transition-colors cursor-pointer"
                    title="Sign Out"
                    @click="signOut"
                >
                    <UIcon name="i-lucide-log-out" class="size-3.5" />
                    <span class="hidden xl:inline">Sign Out</span>
                </button>
            </div>
        </header>

        <!-- Sub-header Row: compact navigation. Links that do not fit move into "More", so the row
             never scrolls sideways and every page stays reachable at any width. -->
        <div class="py-1.5 flex items-center gap-1.5 px-4 sm:px-6 bg-elevated/70">
            <!-- Mobile Navigation Menu Button (Left in front of Dashboard) -->
            <UButton
                class="md:hidden shrink-0"
                size="xs"
                color="primary"
                variant="soft"
                icon="i-lucide-menu"
                :label="$t('layout.open_menu')"
                @click="toggleMenu"
            />

            <nav ref="navRow" class="relative flex flex-1 min-w-0 items-center gap-1">
                <!-- Invisible copy of every link, measured to know how many fit -->
                <div ref="navMeasure" class="invisible absolute flex items-center gap-1 pointer-events-none" aria-hidden="true">
                    <span
                        v-for="item in navItems"
                        :key="item.name"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium whitespace-nowrap"
                    >
                        <UIcon :name="item.icon" class="size-3.5 shrink-0" />
                        <span>{{ item.label }}</span>
                    </span>
                </div>

                <RouterLink
                    v-for="item in visibleNavItems"
                    :key="item.name"
                    :to="{ name: item.name }"
                    :title="item.title"
                    class="group inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors shrink-0"
                    :class="underlineNav
                        ? ['rounded-none', item.active ? 'text-primary shadow-[inset_0_-2px_0_var(--ui-primary)]' : 'text-toned hover:text-highlighted']
                        : ['rounded-md', item.active ? 'bg-primary text-inverted' : 'text-toned hover:text-highlighted hover:bg-(--ui-bg-accented)/60']"
                >
                    <UIcon
                        :name="item.icon"
                        class="size-3.5 shrink-0"
                        :class="item.active ? (underlineNav ? 'text-current' : 'text-inverted') : 'text-dimmed group-hover:text-primary'"
                    />
                    <span>{{ item.label }}</span>
                </RouterLink>

                <UDropdownMenu v-if="overflowNavItems.length" :items="overflowMenu" :content="{ align: 'end' }">
                    <UButton
                        size="xs"
                        :color="overflowActive ? 'primary' : 'neutral'"
                        :variant="overflowActive && !underlineNav ? 'solid' : 'ghost'"
                        trailing-icon="i-lucide-chevron-down"
                        :label="overflowActive?.label ?? $t('layout.more')"
                        class="shrink-0"
                    />
                </UDropdownMenu>
            </nav>
        </div>

        <!-- Global Command Palette / Search Modal -->
        <GlobalSearchModal ref="searchModalRef" />
    </div>
</template>

<script lang="ts" setup>
    import { useResizeObserver } from '@vueuse/core'
    import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import GlobalSearchModal from '@/components/modals/GlobalSearchModal.vue'
    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { useLayout } from '@/layouts/layout'
    import { supportedLocales, setAppLocale, type AvailableLocales } from '@/libs/i18n'
    import { calculateTreeStatusCounts } from '@/libs/status'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { TreeNode } from '@/types/Tree'
    import pkg from '../../../package.json'

    const appVersion = pkg.version

    const { t, locale } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()
    const { toggleMenu } = useLayout()

    /** The Classic theme underlines the active page, as the old app did, instead of filling it. */
    const underlineNav = computed(() => appStore.uiTheme === 'classic')

    const searchModalRef = ref<InstanceType<typeof GlobalSearchModal> | null>(null)
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)

    const openGlobalSearch = (initialQuery?: string) => {
        searchModalRef.value?.open(initialQuery)
    }

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
        // Cmd + K (Mac) or Ctrl + K (Windows/Linux)
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault()
            openGlobalSearch()
            return
        }

        // Quick "/" shortcut when not inside input / textarea / editable
        const targetTag = (e.target as HTMLElement)?.tagName
        const isEditable = (e.target as HTMLElement)?.isContentEditable
        if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTag) && !isEditable) {
            e.preventDefault()
            openGlobalSearch()
        }
    }

    const statusCounts = ref({ up: 0, down: 0, warning: 0, acknowledged: 0, paused: 0, unknown: 0, total: 0 })
    let statusTimer: ReturnType<typeof setInterval> | null = null

    const activeStatusQuery = computed(() => {
        if (route.name !== 'devices') return null
        return typeof route.query.status === 'string' ? route.query.status.toUpperCase() : null
    })

    const selectStatusFilter = (status: 'UP' | 'DOWN' | 'WARNING' | 'ACKNOWLEDGED' | 'PAUSED' | 'UNKNOWN') => {
        void router.push({
            name:  'devices',
            query: { status, t: String(Date.now()) },
        })
    }

    const currentLocaleCode = computed(() => (locale.value as AvailableLocales) || 'en')
    const currentLocaleFlag = computed(() => {
        const found = supportedLocales.find((l) => l.code === currentLocaleCode.value)
        return found ? found.flag : '🌐'
    })

    const languageMenu = computed(() => [
        supportedLocales.map((item) => ({
            label:    `${item.flag} ${item.label}`,
            icon:     currentLocaleCode.value === item.code ? 'i-lucide-check' : undefined,
            onSelect: () => {
                setAppLocale(item.code)
            },
        })),
    ])

    // Short labels keep the row on one line; the full section name is the tooltip
    const NAV_LINKS = [
        { name: 'dashboard', key: 'dashboard', icon: 'i-lucide-layout-dashboard' },
        { name: 'devices', key: 'devices', icon: 'i-lucide-server' },
        { name: 'topology', key: 'topology', icon: 'i-lucide-network' },
        { name: 'ipam', key: 'ipam', icon: 'i-lucide-binary' },
        { name: 'documentation', key: 'documentation', icon: 'i-lucide-file-text' },
        { name: 'security', key: 'security', icon: 'i-lucide-shield-alert' },
        { name: 'syslog', key: 'syslog', icon: 'i-lucide-terminal' },
        { name: 'events', key: 'events', icon: 'i-lucide-activity' },
        { name: 'tools', key: 'tools', icon: 'i-lucide-wrench' },
        { name: 'reports-sla', key: 'sla', icon: 'i-lucide-award' },
        { name: 'templates', key: 'templates', icon: 'i-lucide-boxes' },
        { name: 'users', key: 'users', icon: 'i-lucide-users' },
        { name: 'settings', key: 'settings', icon: 'i-lucide-settings' },
        { name: 'about', key: 'about', icon: 'i-lucide-info' },
    ]

    const navItems = computed(() => NAV_LINKS.map((link) => ({
        name:   link.name,
        icon:   link.icon,
        label:  t(`nav_short.${link.key}`),
        title:  t(`nav.${link.key}`),
        active: route.name === link.name,
    })))

    const navRow = useTemplateRef<HTMLElement>('navRow')
    const navMeasure = useTemplateRef<HTMLElement>('navMeasure')
    const visibleNavCount = ref(NAV_LINKS.length)

    const visibleNavItems = computed(() => navItems.value.slice(0, visibleNavCount.value))
    const overflowNavItems = computed(() => navItems.value.slice(visibleNavCount.value))
    const overflowActive = computed(() => overflowNavItems.value.find((item) => item.active))
    const overflowMenu = computed(() => overflowNavItems.value.map((item) => ({
        label: item.title,
        icon:  item.icon,
        to:    { name: item.name },
        class: item.active ? 'text-primary' : undefined,
    })))

    // Room for the "More" button, which can carry the label of the current page
    const MORE_WIDTH = 150
    const NAV_GAP = 4

    const fitNavItems = () => {
        const row = navRow.value
        const measure = navMeasure.value
        if (!row || !measure) return

        const widths = Array.from(measure.children).map((child) => (child as HTMLElement).offsetWidth + NAV_GAP)
        const available = row.clientWidth
        const total = widths.reduce((sum, width) => sum + width, 0)
        if (total <= available) {
            visibleNavCount.value = widths.length
            return
        }

        let used = MORE_WIDTH
        let count = 0
        while (count < widths.length && used + widths[count] <= available) {
            used += widths[count]
            count++
        }

        visibleNavCount.value = count
    }

    useResizeObserver(navRow, fitNavItems)
    // Labels change width with the language
    watch(locale, () => nextTick(fitNavItems))

    const userName = computed((): string => appStore.userData?.name || appStore.userData?.username || 'admin')

    const initials = computed((): string => userName.value
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join(''))

    const signOut = () => {
        appStore.setLogout()
        void router.push({ name: 'login' })
    }

    const systemMenu = computed(() => [
        [
            {
                label:    'Global Settings',
                icon:     'i-lucide-settings',
                onSelect: () => void router.push({ name: 'settings' }),
            },
            {
                label:    'SLA & Availability Reports',
                icon:     'i-lucide-award',
                onSelect: () => void router.push({ name: 'reports-sla' }),
            },
            {
                label:    'Network & Diagnostics Tools',
                icon:     'i-lucide-wrench',
                onSelect: () => void router.push({ name: 'tools' }),
            },
            {
                label:    'Audit Change Log',
                icon:     'i-lucide-clipboard-list',
                onSelect: () => void router.push({ name: 'settings', query: { tab: 'audit' } }),
            },
            {
                label:    'Backup & Recovery',
                icon:     'i-lucide-database',
                onSelect: () => void router.push({ name: 'settings', query: { tab: 'backup' } }),
            },
        ],
    ])

    const userMenu = computed(() => [
        [
            {
                label:    t('general.sign_out'),
                icon:     'i-lucide-log-out',
                onSelect: signOut,
            },
        ],
    ])

    const loadStatus = async () => {
        const { response, data } = await fetchStore.fetch('/api/v1/tree').get().json<TreeNode[]>()
        if (response.value?.status === 200 && data.value) {
            const counts = calculateTreeStatusCounts(data.value)
            statusCounts.value = {
                up:           counts.up,
                down:         counts.down,
                warning:      counts.warning,
                acknowledged: counts.acknowledged,
                paused:       counts.paused,
                unknown:      counts.unknown,
                total:        counts.total,
            }
        }
    }

    const onOpenSearchEvent = (e: Event) => {
        const customEv = e as CustomEvent<{ query?: string }>
        openGlobalSearch(customEv.detail?.query)
    }

    onMounted(() => {
        // Poppins arrives after the first layout and widens the labels
        void document.fonts?.ready.then(fitNavItems)
        void loadStatus()
        statusTimer = setInterval(loadStatus, 30000)
        window.addEventListener('nervuseye:tree-updated', loadStatus)
        window.addEventListener('nervuseye:open-search', onOpenSearchEvent)
        window.addEventListener('keydown', handleGlobalKeyDown)
    })

    onUnmounted(() => {
        if (statusTimer) clearInterval(statusTimer)
        window.removeEventListener('nervuseye:tree-updated', loadStatus)
        window.removeEventListener('nervuseye:open-search', onOpenSearchEvent)
        window.removeEventListener('keydown', handleGlobalKeyDown)
    })
</script>
